import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { Button, Container } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ApiError, AuthService, ResendEmailVerification } from '../../api';
import { Logo } from '../../app/components/Logo';
import { SessionContext } from '../../app/contexts/SessionContext';
import { useDocumentTitle } from '../../app/hooks/use-document-title';
import { handleApiError } from '../../app/utils/errorhandling';

const PendingEmailConfirmation = () => {
  const navigate = useNavigate();

  const mutationResendEmailVerification = useMutation({
    mutationFn: async (data: ResendEmailVerification) => {
      await AuthService.authRegistrationResendEmailCreate({
        requestBody: data,
      });
    },
    onSuccess: () => {
      toast.success('Verification email sent successfully');
    },
    onError: (errors: ApiError) => {
      handleApiError(errors, {
        messageOverride: 'Unable to send verification email. Please try again later.',
      });
    },
  });

  const resendEmailVerification = async () => {
    // deprecating last_signin_email in favor of authLastSigninEmail
    const email =
      localStorage.getItem('authLastSigninEmail') || localStorage.getItem('last_signin_email');
    if (!email) {
      toast.error('Something went wrong. Please try logging in again.');
      return navigate('/login');
    }

    await mutationResendEmailVerification.mutateAsync({ email });
  };

  return (
    <div className="login-bg-container">
      <Container>
        <Card>
          <Card.Body>
            <div className="logo-container">
              <Logo className="logo" variant="color" />
            </div>
            <div className="text-center">
              <h1>Email Confirmation</h1>
              <p>Please check your email and click on the link we sent to validate your account!</p>
              <p className="text-muted small">
                The link is valid for 48h, resend to get a new one.
                <br />
                <Button
                  variant="outline-primary"
                  className="mt-4"
                  onClick={resendEmailVerification}
                >
                  Resend email
                </Button>
              </p>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export const EmailConfirmation = () => {
  const { user } = useContext(SessionContext);
  const navigate = useNavigate();
  useDocumentTitle('Email Confirmation');

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  return <PendingEmailConfirmation />;
};
